import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { generateBulkMessages } from '../../../services/communicationAIService';

const BulkSendModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    channel: 'whatsapp',
    messageTemplate: '',
    context: '',
    tone: 'professional',
  });
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [personalizedMessages, setPersonalizedMessages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Mock recipients data
  const mockRecipients = [
    { id: 1, name: 'أحمد محمد', phone: '+966501234567', type: 'مشتري', status: 'نشط' },
    { id: 2, name: 'فاطمة علي', phone: '+966507654321', type: 'مستأجر', status: 'نشط' },
    { id: 3, name: 'خالد عبدالله', phone: '+966509876543', type: 'مستثمر', status: 'نشط' },
    { id: 4, name: 'نورة سعيد', phone: '+966502345678', type: 'مشتري', status: 'متابعة' },
    { id: 5, name: 'عمر حسن', phone: '+966508765432', type: 'مستأجر', status: 'نشط' },
  ];

  const channelOptions = [
    { value: 'whatsapp', label: 'واتساب' },
    { value: 'sms', label: 'رسالة نصية' },
    { value: 'email', label: 'بريد إلكتروني' },
  ];

  const toneOptions = [
    { value: 'professional', label: 'رسمي' },
    { value: 'friendly', label: 'ودي' },
    { value: 'urgent', label: 'عاجل' },
  ];

  const handleRecipientToggle = (recipientId) => {
    setSelectedRecipients(prev =>
      prev?.includes(recipientId)
        ? prev?.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecipients?.length === mockRecipients?.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(mockRecipients?.map(r => r?.id));
    }
  };

  const handleGenerateMessages = async () => {
    if (!formData?.messageTemplate || selectedRecipients?.length === 0) {
      alert('الرجاء إدخال قالب الرسالة واختيار مستلمين');
      return;
    }

    setIsGenerating(true);
    try {
      const recipients = mockRecipients?.filter(r => selectedRecipients?.includes(r?.id));
      const messages = await generateBulkMessages({
        recipients,
        messageTemplate: formData?.messageTemplate,
        context: formData?.context,
        tone: formData?.tone,
      });
      setPersonalizedMessages(messages);
      setStep(3);
    } catch (error) {
      alert(error?.message || 'فشل في توليد الرسائل');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendAll = async () => {
    setIsSending(true);
    try {
      // Simulate sending
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert(`تم إرسال ${personalizedMessages?.length} رسالة بنجاح!`);
      onClose();
    } catch (error) {
      alert('فشل في إرسال الرسائل');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="إرسال جماعي" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {[1, 2, 3]?.map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}
              >
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-blue-500' : 'bg-gray-700'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Message Template */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">خطوة 1: قالب الرسالة</h3>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                قناة الاتصال
              </label>
              <Select
                value={formData?.channel}
                onChange={(e) => setFormData({ ...formData, channel: e?.target?.value })}
                options={channelOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                نبرة الرسالة
              </label>
              <Select
                value={formData?.tone}
                onChange={(e) => setFormData({ ...formData, tone: e?.target?.value })}
                options={toneOptions}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                قالب الرسالة
              </label>
              <textarea
                value={formData?.messageTemplate}
                onChange={(e) => setFormData({ ...formData, messageTemplate: e?.target?.value })}
                placeholder="مرحباً [اسم العميل]، نود إعلامك بعروضنا الجديدة..."
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                rows={5}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                السياق الإضافي
              </label>
              <textarea
                value={formData?.context}
                onChange={(e) => setFormData({ ...formData, context: e?.target?.value })}
                placeholder="أي تفاصيل إضافية لتخصيص الرسائل..."
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                rows={3}
              />
            </div>

            <Button onClick={() => setStep(2)} className="w-full">
              التالي: اختيار المستلمين
            </Button>
          </div>
        )}

        {/* Step 2: Select Recipients */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">خطوة 2: اختيار المستلمين</h3>

            <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
              <span className="text-sm text-gray-300">
                تم اختيار {selectedRecipients?.length} من {mockRecipients?.length}
              </span>
              <Button onClick={handleSelectAll} variant="secondary" size="sm">
                {selectedRecipients?.length === mockRecipients?.length ? 'إلغاء الكل' : 'اختيار الكل'}
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {mockRecipients?.map((recipient) => (
                <div
                  key={recipient?.id}
                  className="flex items-center gap-3 p-3 bg-gray-800/30 border border-gray-700 rounded-lg hover:bg-gray-800/50 transition-colors"
                >
                  <Checkbox
                    checked={selectedRecipients?.includes(recipient?.id)}
                    onChange={() => handleRecipientToggle(recipient?.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{recipient?.name}</span>
                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                        {recipient?.type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400">{recipient?.phone}</div>
                  </div>
                  <div className="text-xs text-green-400">{recipient?.status}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="secondary" className="flex-1">
                السابق
              </Button>
              <Button
                onClick={handleGenerateMessages}
                disabled={selectedRecipients?.length === 0 || isGenerating}
                className="flex-1"
              >
                {isGenerating ? (
                  <>
                    <Icon name="Loader" size={18} className="animate-spin" />
                    <span>جاري التخصيص...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Sparkles" size={18} />
                    <span>تخصيص بالذكاء الاصطناعي</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Send */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">خطوة 3: مراجعة وإرسال</h3>

            <div className="max-h-[500px] overflow-y-auto space-y-3">
              {personalizedMessages?.map((msg, index) => (
                <div
                  key={index}
                  className="p-4 bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-lg"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="User" size={16} color="#3B82F6" />
                    <span className="font-medium text-blue-400">{msg?.recipientName}</span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{msg?.message}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="secondary" className="flex-1">
                السابق
              </Button>
              <Button
                onClick={handleSendAll}
                disabled={isSending}
                className="flex-1"
                variant="primary"
              >
                {isSending ? (
                  <>
                    <Icon name="Loader" size={18} className="animate-spin" />
                    <span>جاري الإرسال...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={18} />
                    <span>إرسال الكل ({personalizedMessages?.length})</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkSendModal;